import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { FormControl } from '@angular/forms';
import { RadioInputOptions } from '@cp/ui/radio-input/radio-input.component';
import { CurrencyCode, Division } from '@cp/shared/data-access/shared/enums';
import { SharedSelectors } from '@cp/shared/data-access/+state/selectors';
import { DivisionService } from '@cp/shared/data-access/division/division.service';
import { PayRateSection } from '@cp/shared/data-access/http/dashboard/dashboard-http.models';
import {
  PayRatesType,
  PayRatesUrl,
} from '../../interfaces/pay-rates.interface';

interface PayRatesViewModel {
  sections: Array<PayRateSection>;
  division: Division;
  currencyCode: CurrencyCode;
  payRatesUrl: PayRatesUrl;
}

@Component({
  selector: 'cp-pay-rates',
  templateUrl: './pay-rates.component.html',
  styleUrls: ['./pay-rates.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayRatesComponent implements OnInit, OnDestroy {
  private readonly _showPrivate = new BehaviorSubject(false);
  private readonly _destroy$ = new Subject<void>();

  public readonly options: Array<RadioInputOptions> = [
    { label: 'Standard Rate', value: false },
    { label: 'Private Rate', value: true },
  ];
  public readonly control = new FormControl<boolean>(this._showPrivate.value);
  public readonly vm$: Observable<PayRatesViewModel> = combineLatest([
    this._store.select(SharedSelectors.selectPayRates),
    this._store.select(SharedSelectors.selectDivision),
    this._showPrivate.asObservable(),
    this._store.select(SharedSelectors.selectDivisionCurrencyCode),
  ]).pipe(
    map(([{ sections }, division, showPrivate, currencyCode]) => ({
      sections: this._filterPrivateRates(sections ?? [], showPrivate),
      division,
      currencyCode,
      payRatesUrl: {
        standard: this._getPayRatesUrl(division, 'standardPayRatesUrl'),
        specialist: this._getPayRatesUrl(division, 'specialistPayRatesUrl'),
        private: this._getPayRatesUrl(division, 'privatePayRatesUrl'),
      },
    })),
  );
  public readonly division = Division;

  constructor(
    private _store: Store,
    private _divisionService: DivisionService,
  ) {}

  public ngOnInit(): void {
    this.control.valueChanges
      .pipe(
        tap((showPrivate) => this._showPrivate.next(showPrivate ?? false)),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _getPayRatesUrl(
    division: Division,
    payRatesType: PayRatesType,
  ): string {
    return this._divisionService.getConfigEntry(payRatesType, division);
  }

  private _filterPrivateRates(
    rates: Array<PayRateSection>,
    showPrivate: boolean,
  ): Array<PayRateSection> {
    return rates.filter(({ isPrivate }) => isPrivate === showPrivate);
  }
}
